import React, { MouseEventHandler, useRef } from 'react';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { useFocus, useHover } from '../../../hooks/useElementState';
import { applyComponentTheme } from '../../../theme';
import Icon from '../../Icon';
import {
  ActionCardContainer,
  ActionCardContainerProps,
  Body,
  ChevronWrapper,
  Heading,
  HeadingContainer,
  HeadingWrapper,
  IconWrapper,
  Subheading,
} from './styles';

export type ActionCardProps = {
  href: string;
  icon?: string;
  heading: string;
  subheading?: string;
  body?: string;
  onClick?: MouseEventHandler;
  download?: boolean | string;
} & ActionCardContainerProps &
  ComponentProps;

const ActionCard: React.FC<ActionCardProps> = ({
  href,
  icon,
  heading,
  subheading,
  body,
  disabled,
  onClick,
  download,
  ...props
}) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const isHovering = useHover(ref);
  const isFocused = useFocus(ref);
  const isUnfocusable = disabled ? { tabIndex: -1 } : { tabIndex: 0 };

  return (
    <ActionCardContainer
      {...componentProps(props)}
      href={disabled ? undefined : href}
      onClick={onClick}
      aria-label={heading}
      disabled={disabled}
      ref={ref}
      download={download}
      {...isUnfocusable}>
      <div {...isUnfocusable}>
        <HeadingWrapper>
          {icon && (
            <IconWrapper>
              <Icon
                name={icon}
                size="large"
                displayVariant={disabled || !(isHovering || isFocused)}
              />
            </IconWrapper>
          )}
          <HeadingContainer>
            <Heading>{heading}</Heading>
            {subheading && <Subheading>{subheading}</Subheading>}
          </HeadingContainer>
          <ChevronWrapper>
            <Icon name="chevron" size="small" />
          </ChevronWrapper>
        </HeadingWrapper>
        {body && <Body>{body}</Body>}
      </div>
    </ActionCardContainer>
  );
};

export default applyComponentTheme(ActionCard, 'ActionCard');
